<template>
  <div class="agentwise-report py-3">
    <header class="ar-toolbar">
      <div class="ar-toolbar__title">
        <span class="ar-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Agentwise Report</h1>
          <p>Daily call → order → bottle performance for a telesales agent</p>
        </div>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="ar-alert">{{ errorMessage }}</CAlert>

    <section class="ar-section">
      <header class="ar-section__head">
        <div class="ar-section__head-left">
          <h2>Filters</h2>
          <small class="ar-section__hint">{{ rangeLabel }}</small>
        </div>
        <button
          type="button"
          class="ar-btn ar-btn--primary"
          :class="{ 'is-loading': isLoading }"
          :disabled="isLoading"
          @click="loadReport"
        >
          <svg v-if="!isLoading" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M3 3v18h18" />
            <path d="M7 14l4-4 4 4 5-6" />
          </svg>
          <span>{{ isLoading ? 'Loading…' : 'Load Report' }}</span>
        </button>
      </header>
      <div class="ar-section__body">
        <div class="ar-filters">
          <div class="ar-field ar-field--range">
            <label class="ar-label">Period</label>
            <DateRangePicker v-model="dateRange" variant="form" :presets="false" />
          </div>
          <div class="ar-field ar-field--agent">
            <label class="ar-label">Agent</label>
            <AppDropdown
              :value="userId"
              :options="userOptions"
              :searchable="users.length > 8"
              search-placeholder="Find agent…"
              placeholder="All Telesales"
              @input="userId = $event"
            />
          </div>
          <div class="ar-field ar-field--presets">
            <label class="ar-label" aria-hidden="true">&nbsp;</label>
            <div class="ar-presets" role="group" aria-label="Quick date ranges">
              <button
                v-for="preset in presets"
                :key="preset.key"
                type="button"
                class="ar-preset"
                :class="{ 'is-active': activePreset === preset.key }"
                @click="applyPreset(preset.key)"
              >
                {{ preset.label }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div v-if="summary" class="ar-kpis">
      <div class="kpi-tile kpi-tile--calls">
        <span class="kpi-tile__label">Calls</span>
        <span class="kpi-tile__value">{{ formatNumber(summary.chart_calls) }}</span>
        <span class="kpi-tile__sub">{{ rangeLabel }}</span>
      </div>
      <div class="kpi-tile kpi-tile--orders">
        <span class="kpi-tile__label">Orders</span>
        <span class="kpi-tile__value">{{ formatNumber(summary.chart_orders) }}</span>
        <span class="kpi-tile__sub">{{ ordersPerDayLabel }}</span>
      </div>
      <div class="kpi-tile kpi-tile--bottles">
        <span class="kpi-tile__label">Bottles</span>
        <span class="kpi-tile__value">{{ formatNumber(summary.chart_bottles) }}</span>
        <span class="kpi-tile__sub">{{ bottlesPerOrderLabel }}</span>
      </div>
      <div class="kpi-tile kpi-tile--conversion">
        <span class="kpi-tile__label">Conversion</span>
        <span class="kpi-tile__value">{{ formatPercent(summary.chart_conversion) }}</span>
        <span class="kpi-tile__sub">Orders ÷ calls</span>
      </div>
    </div>

    <BaseTable
      title="Daily Breakdown"
      :items="tableRows"
      :columns="columns"
      :loading="isLoading"
      empty-text="No agent activity in this range."
    >
      <template #toolbar>
        <span v-if="rows.length" class="ar-totals">
          <span v-if="bestDay" class="ar-totals__chip">Top day <strong>{{ bestDay.date }}</strong> · {{ formatNumber(bestDay.total_order) }} orders</span>
        </span>
      </template>

      <template #cell-conversion="{ row }">
        <span :class="['ar-conv', conversionClass(row.conversion_value)]">{{ row.conversion_display }}</span>
      </template>
    </BaseTable>
  </div>
</template>

<script>
import axios from 'axios'
import AppDropdown from '@/components/AppDropdown.vue'
import DateRangePicker from '@/components/DateRangePicker.vue'
import BaseTable from '@/components/BaseTable.vue'

export default {
  name: 'AgentwiseReport',
  components: { AppDropdown, DateRangePicker, BaseTable },
  data () {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const sevenAgo = new Date(today)
    sevenAgo.setDate(today.getDate() - 6)
    return {
      fromDate: this.toIsoStatic(sevenAgo),
      toDate: this.toIsoStatic(today),
      userId: 0,
      users: [],
      rows: [],
      summary: null,
      isLoading: false,
      errorMessage: '',
      presets: [
        { key: 'today', label: 'Today' },
        { key: '7d', label: 'Last 7 Days' },
        { key: '30d', label: 'Last 30 Days' },
        { key: 'mtd', label: 'This Month' },
        { key: 'ytd', label: 'This Year' }
      ],
      columns: [
        { key: 'date', label: 'Date' },
        { key: 'total_calls', label: 'Total Calls', align: 'right', format: 'number' },
        { key: 'total_order', label: 'Total Orders', align: 'right', format: 'number' },
        { key: 'total_bottles', label: 'Total Bottles', align: 'right', format: 'number' },
        { key: 'conversion', label: 'Conversion', align: 'right' }
      ]
    }
  },
  computed: {
    userOptions () {
      return [
        { value: 0, label: 'All Telesales' },
        ...this.users.map((u) => ({ value: u.id, label: u.name }))
      ]
    },
    dateRange: {
      get () {
        return { fromDate: this.fromDate, toDate: this.toDate }
      },
      set (value) {
        this.fromDate = (value && value.fromDate) || ''
        this.toDate = (value && value.toDate) || ''
      }
    },
    rangeLabel () {
      if (!this.fromDate || !this.toDate) return ''
      if (this.fromDate === this.toDate) return this.formatDisplay(this.fromDate)
      return `${this.formatDisplay(this.fromDate)} → ${this.formatDisplay(this.toDate)}`
    },
    activePreset () {
      if (!this.fromDate || !this.toDate) return ''
      for (const preset of this.presets) {
        const range = this.rangeForPreset(preset.key)
        if (range && range.fromDate === this.fromDate && range.toDate === this.toDate) {
          return preset.key
        }
      }
      return ''
    },
    tableRows () {
      return this.rows.map((row) => ({
        ...row,
        conversion_value: this.parsePercent(row.conversion),
        conversion_display: this.formatPercent(row.conversion)
      }))
    },
    bestDay () {
      if (!this.rows.length) return null
      return this.rows.reduce((best, row) => {
        const orders = Number(row.total_order) || 0
        if (!best || orders > (Number(best.total_order) || 0)) return row
        return best
      }, null)
    },
    daysInRange () {
      if (!this.rows.length) return 0
      return this.rows.length
    },
    ordersPerDayLabel () {
      if (!this.summary || !this.daysInRange) return 'Across the period'
      const avg = (Number(this.summary.chart_orders) || 0) / this.daysInRange
      return `${avg.toFixed(1)} / day average`
    },
    bottlesPerOrderLabel () {
      if (!this.summary) return 'Across the period'
      const orders = Number(this.summary.chart_orders) || 0
      const bottles = Number(this.summary.chart_bottles) || 0
      if (!orders) return 'Across the period'
      return `${(bottles / orders).toFixed(2)} / order`
    }
  },
  created () {
    this.loadUsers()
    this.loadReport()
  },
  methods: {
    authToken () {
      return localStorage.getItem('api_token')
    },
    toIsoStatic (date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    toIso (date) {
      return this.toIsoStatic(date)
    },
    formatDisplay (iso) {
      if (!iso) return ''
      const [y, m, d] = iso.split('-')
      if (!y || !m || !d) return iso
      return `${d}-${m}-${y}`
    },
    formatNumber (value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return n.toLocaleString('en-IN')
    },
    parsePercent (raw) {
      if (raw == null) return null
      const n = parseFloat(String(raw).replace(/[^0-9.+-]/g, ''))
      if (!Number.isFinite(n)) return null
      return n <= 1 ? n * 100 : n
    },
    formatPercent (value) {
      const n = this.parsePercent(value)
      if (n == null) return '—'
      return `${n.toFixed(2)}%`
    },
    conversionClass (value) {
      if (value == null) return 'ar-conv--muted'
      if (value >= 25) return 'ar-conv--high'
      if (value >= 10) return 'ar-conv--mid'
      return 'ar-conv--low'
    },
    rangeForPreset (key) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const start = new Date(today)
      const end = new Date(today)
      switch (key) {
        case 'today':
          break
        case '7d':
          start.setDate(today.getDate() - 6)
          break
        case '30d':
          start.setDate(today.getDate() - 29)
          break
        case 'mtd':
          start.setDate(1)
          break
        case 'ytd':
          start.setMonth(0, 1)
          break
        default:
          return null
      }
      return { fromDate: this.toIso(start), toDate: this.toIso(end) }
    },
    applyPreset (key) {
      const range = this.rangeForPreset(key)
      if (!range) return
      this.fromDate = range.fromDate
      this.toDate = range.toDate
    },
    loadUsers () {
      axios.get(`${this.$apiAdress}/api/users/telesales?token=${this.authToken()}`)
        .then((response) => {
          this.users = Array.isArray(response.data) ? response.data : []
        })
        .catch((error) => {
          console.log(error)
        })
    },
    loadReport () {
      this.errorMessage = ''
      this.isLoading = true
      axios.post(
        `${this.$apiAdress}/api/reports/agentwise?token=${this.authToken()}`,
        { user_id: Number(this.userId), from_date: this.fromDate, to_date: this.toDate }
      )
        .then((response) => {
          const data = response.data || {}
          this.rows = Array.isArray(data.table_data) ? data.table_data : []
          this.summary = data
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = error?.response?.data?.message || 'Unable to load agentwise report.'
          this.rows = []
          this.summary = null
        })
        .finally(() => {
          this.isLoading = false
        })
    }
  }
}
</script>

<style scoped>
.agentwise-report {
  padding: 0.25rem 0;
}

.ar-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.ar-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.ar-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.ar-toolbar__title p {
  margin: 0.1rem 0 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.ar-toolbar__accent {
  width: 4px;
  height: 32px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
}

.ar-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

.ar-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
}

.ar-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.55rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  border-radius: 12px 12px 0 0;
  flex-wrap: wrap;
}

.ar-section__head-left {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  min-width: 0;
}

.ar-section__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.ar-section__hint {
  color: #6b7280;
  font-size: 0.78rem;
}

.ar-section__body {
  padding: 1rem;
}

.ar-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.ar-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
}

.ar-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.ar-field--range {
  flex: 0 1 360px;
  min-width: 260px;
}

.ar-field--agent {
  flex: 0 1 240px;
  min-width: 200px;
}

.ar-field--presets {
  flex: 1 1 320px;
  min-width: 0;
}

.ar-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  min-height: 36px;
  align-items: center;
}

.ar-preset {
  display: inline-flex;
  align-items: center;
  height: 30px;
  padding: 0 0.85rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.ar-preset:hover {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.ar-preset:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.ar-preset.is-active {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border-color: transparent;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22);
}

.ar-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  height: 36px;
  padding: 0 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.ar-btn--primary {
  color: #ffffff;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}

.ar-btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.ar-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.28);
}

.ar-btn:disabled,
.ar-btn.is-loading {
  opacity: 0.85;
  cursor: progress;
}

.ar-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.85rem;
  margin-bottom: 1rem;
}

.kpi-tile {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  padding: 0.85rem 1rem;
  border-left-width: 4px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.kpi-tile--calls      { border-left-color: #2563eb; }
.kpi-tile--orders     { border-left-color: #0e5b46; }
.kpi-tile--bottles    { border-left-color: #f59e0b; }
.kpi-tile--conversion { border-left-color: #7c3aed; }

.kpi-tile__label {
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
}

.kpi-tile__value {
  font-size: 1.55rem;
  font-weight: 800;
  color: #1f2a44;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
}

.kpi-tile--orders .kpi-tile__value { color: #0e5b46; }
.kpi-tile--conversion .kpi-tile__value { color: #5b21b6; }

.kpi-tile__sub {
  font-size: 0.74rem;
  color: #6b7280;
  margin-top: 2px;
}

.ar-totals {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.ar-totals__chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #4f5560;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
}

.ar-totals__chip strong {
  color: #1f2a44;
  font-variant-numeric: tabular-nums;
}

.ar-conv {
  display: inline-flex;
  align-items: center;
  padding: 0.1rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.ar-conv--low    { background: #ffe4e6; color: #9f1239; }
.ar-conv--mid    { background: #fef3c7; color: #92400e; }
.ar-conv--high   { background: #dff5ea; color: #0e5b46; }
.ar-conv--muted  { background: #eef1f7; color: #4b5563; }

@media (max-width: 991px) {
  .ar-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 575px) {
  .ar-kpis {
    grid-template-columns: 1fr;
  }
}
</style>
